import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../config";

// The client portal API (djr-be routes/portal.js). Design:
// djr-be/_docs/for-cowork/2026-09-11_2339_CLIENT_PORTAL_DESIGN.md, section 5.1.
//
// Deliberately its OWN fetchBaseQuery, never baseQueryWithReauth:
// - baseQueryWithReauth attaches the admin Bearer token from localStorage to
//   every request. A client portal request must be anonymous, so no
//   Authorization header is ever sent from here.
// - on an unauthorized answer baseQueryWithReauth tries a refresh and, when
//   that fails, deletes the admin token and refresh token from localStorage.
//   A stale client link opened in Andrew's own browser would log him out of
//   the admin. The portal backend never answers unauthorized (it answers 403
//   for a missing or wrong key), so this is defence in depth on both sides.
//
// No cookies either (credentials: "omit"): the signed link in ?k= is the only
// way in.
//
// The base URL is the build-time one (REACT_APP_API_URL through src/config),
// read once here. It never follows window.__API_BASE_URL, which BrandContext
// sets later from the brand document.
const PORTAL_API_BASE_URL = config?.api?.baseURL ?? "";

// GET /portal/:eventId/timeline-pdf?k=   the newest saved client timeline
// PDF, served inline (download: true adds &download=1 for an attachment).
// The key travels in the query string, the same as every other portal read,
// so the page links to this URL directly: a plain link opens it in a new tab
// on the tap itself (iOS safe) and no request is made until the client taps.
export const portalTimelinePdfUrl = ({ eventId, accessKey, download = false } = {}) => {
  const params = new URLSearchParams({ k: accessKey ?? "" });
  if (download) params.set("download", "1");
  return `${PORTAL_API_BASE_URL}/portal/${encodeURIComponent(eventId ?? "")}/timeline-pdf?${params.toString()}`;
};

// GET /portal/:eventId answers { event, timelinePdf }: the timeline PDF
// summary sits NEXT TO the event. Every consumer keeps the event shape; the
// summary rides along as event.timelinePdf (null when absent).
export const portalEventFromResponse = (response) => {
  const event = response?.event ?? null;
  if (!event || typeof event !== "object") return null;
  return { ...event, timelinePdf: response?.timelinePdf ?? null };
};

const portalBaseQuery = fetchBaseQuery({
  baseUrl: PORTAL_API_BASE_URL,
  credentials: "omit",
  prepareHeaders: (headers) => {
    headers.delete("Authorization");
    return headers;
  },
});

const portalApi = createApi({
  reducerPath: "portal",
  baseQuery: portalBaseQuery,
  // A client's booking view is never kept around after the page leaves it.
  keepUnusedDataFor: 0,
  // The activity list refreshes after a save and after a request. The event
  // itself is never invalidated: a refetch of it would replace the page.
  tagTypes: ["PortalActivity"],
  endpoints: (builder) => ({
    // GET /portal/:eventId?k= answers { event } (djr-be helpers/portalEventView.js)
    getPortalEvent: builder.query({
      query: ({ eventId, accessKey }) => ({
        url: `/portal/${encodeURIComponent(eventId ?? "")}`,
        params: { k: accessKey ?? "" },
        cache: "no-store",
      }),
      transformResponse: portalEventFromResponse,
    }),
    // PATCH /portal/:eventId?k=   body { clientPortal: { <writable fields> } }
    // The client's own planning data (djr-be helpers/portalClientWrite.js).
    // Answers { msg, clientPortal, updatedAtPacific }.
    updatePortalClientData: builder.mutation({
      query: ({ eventId, accessKey, clientPortal }) => ({
        url: `/portal/${encodeURIComponent(eventId ?? "")}`,
        method: "PATCH",
        params: { k: accessKey ?? "" },
        body: { clientPortal: clientPortal ?? {} },
        cache: "no-store",
      }),
      invalidatesTags: (result) => (result ? ["PortalActivity"] : []),
    }),
    // POST /portal/:eventId/request?k=   body { kind: "change" | "question", message }
    // Saved first, then Andrew is emailed. Answers 201 { msg, request, notified }.
    // A request never changes the event.
    createPortalRequest: builder.mutation({
      query: ({ eventId, accessKey, kind, message }) => ({
        url: `/portal/${encodeURIComponent(eventId ?? "")}/request`,
        method: "POST",
        params: { k: accessKey ?? "" },
        body: { kind, message },
        cache: "no-store",
      }),
      invalidatesTags: (result) => (result ? ["PortalActivity"] : []),
    }),
    // POST /portal/:eventId/parse?k=   body { text }
    // The capture box (djr-be controllers/portalCaptureController.js). Answers
    // { ops, unmatched, parsesLeft }; writes nothing but a parse counter, so
    // it invalidates nothing. Applying the ops is the client's job, through
    // the two mutations above.
    parsePortalCapture: builder.mutation({
      query: ({ eventId, accessKey, text }) => ({
        url: `/portal/${encodeURIComponent(eventId ?? "")}/parse`,
        method: "POST",
        params: { k: accessKey ?? "" },
        body: { text },
        cache: "no-store",
      }),
    }),
    // GET /portal/:eventId/activity?k=   answers { activity, count, cap },
    // newest first (djr-be helpers/portalActivity.js).
    getPortalActivity: builder.query({
      query: ({ eventId, accessKey }) => ({
        url: `/portal/${encodeURIComponent(eventId ?? "")}/activity`,
        params: { k: accessKey ?? "" },
        cache: "no-store",
      }),
      transformResponse: (response) =>
        Array.isArray(response?.activity) ? response.activity : [],
      providesTags: ["PortalActivity"],
    }),
  }),
});

export const {
  useGetPortalEventQuery,
  useUpdatePortalClientDataMutation,
  useCreatePortalRequestMutation,
  useParsePortalCaptureMutation,
  useGetPortalActivityQuery,
} = portalApi;

export default portalApi;
